import { Chart, registerables } from "chart.js"
Chart.register(...registerables);
import { Controller } from "@hotwired/stimulus"
import deepmerge from "deepmerge"
import { drilldownInteractionConfig } from "../lib/chart_drilldown"

const cavalryFontFamily = "'Plus Jakarta Sans', Inter, Helvetica, Arial, sans-serif"

const doughnutLabel = {
  id: 'doughnutLabel',
  beforeDatasetsDraw(chart) {
    const { ctx } = chart;
    const label = chart?.options?.plugins?.doughnutLabel?.label;
    const color = chart?.options?.plugins?.doughnutLabel?.color || '#000000';

    if (!label) return;

    ctx.save();
    const firstData = chart.getDatasetMeta(0).data[0];
    const xCoor = firstData?.x;
    const yCoor = firstData?.y;

    const fontSize = window.innerWidth < 640 ? 20 : 24;

    ctx.font = `bold ${fontSize}px ${cavalryFontFamily}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = color;
    ctx.fillText(`${label}`, xCoor, yCoor);
    ctx.restore();
  }
};

export function mobileLegendConfig(config, isMobile) {
  const legendPosition = config?.options?.plugins?.legend?.position
  if (!isMobile || !['left', 'right'].includes(legendPosition)) return {}

  return {
    options: {
      plugins: {
        legend: {
          position: 'bottom'
        }
      }
    }
  }
}

export default class extends Controller {
  static values = {
    config: Object,
    format: String,
    darkBackground: {
      type: Boolean,
      default: false
    },
    doughnutLabel: {
      type: String,
      default: ''
    },
    doughnutLabelColor: {
      type: String,
      default: '#000000'
    },
    drilldownUrl: {
      type: String,
      default: ''
    }
  }

  connect() {
    this.boundReconnect ??= this.reconnect.bind(this)
    this.element.addEventListener("turbo:before-morph-element", this.boundReconnect)

    this.destroyChart()
    const ctx = this.element.getContext('2d')
    this.chart = new Chart(ctx, this.config())
  }

  disconnect() {
    if (this.boundReconnect) {
      this.element.removeEventListener("turbo:before-morph-element", this.boundReconnect)
    }

    this.destroyChart()
  }

  reconnect(event) {
    this.destroyChart()
    this.element.replaceWith(event.detail.newElement)
  }

  destroyChart() {
    if (!this.chart) return

    this.chart.destroy()
    this.chart = null
  }

  config() {
    let originalConfig = this.configValue
    let formatConfig = {}
    const isMobile = window.innerWidth < 640

    if (this.hasMixedDatasetFormats(originalConfig)) {
      formatConfig = this.perDatasetFormatConfig(originalConfig)
    } else if (this.formatValue === 'currency') {
      formatConfig = this.currencyConfig()
    } else if (this.formatValue === 'percentage') {
      formatConfig = this.percentageConfig()
    }

    if (originalConfig.type === 'bar') {
      if (this.isResolutionTypeChart()) {
        formatConfig = deepmerge(formatConfig, this.resolutionTypeConfig())
        formatConfig = deepmerge(formatConfig, this.mobileConfig(isMobile))

        if (originalConfig.data && originalConfig.data.datasets && originalConfig.data.datasets.length > 0) {
          originalConfig.data.datasets.forEach(dataset => {
            dataset.barThickness = isMobile ? 20 : 35;
          });
        }
      }
    }

    if (originalConfig.type === 'doughnut' && this.doughnutLabelValue) {
      // Ensure plugins array exists before pushing new plugin
      originalConfig.plugins = [...(originalConfig.plugins || []), doughnutLabel]

      // Initialize options and plugins objects if they don't exist
      originalConfig.options = originalConfig.options || {};
      originalConfig.options.plugins = originalConfig.options.plugins || {};

      originalConfig.options.plugins.doughnutLabel = {
        label: this.doughnutLabelValue,
        color: this.doughnutLabelColorValue
      };
    }

    formatConfig = deepmerge(formatConfig, mobileLegendConfig(originalConfig, isMobile))

    // Handlers always attached; they no-op when drilldownUrlValue is empty so a drilldown
    // generated after page render (job finishes late) activates clicks without re-rendering.
    formatConfig = deepmerge(formatConfig, drilldownInteractionConfig(() => this.drilldownUrlValue))

    return deepmerge(originalConfig, formatConfig)
  }

  isResolutionTypeChart() {
    return this.darkBackgroundValue
  }

  resolutionTypeConfig() {
    return {
      options: {
        responsive: true,
        scales: {
          x: {
            grid: { display: false },
            border: { color: 'white' },
            ticks: {
              color: 'white',
              font: {
                size: 20,
                family: cavalryFontFamily
              }
            }
          },
          y: {
            grid: { display: false },
            border: { display: false },
            ticks: {
              color: 'white',
              font: {
                size: 20,
                family: cavalryFontFamily
              }
            }
          }
        },
        plugins: {
          legend: { display: false },
          title: { display: false },
          tooltip: { enabled: false }
        }
      }
    }
  }

  mobileConfig(isMobile) {
    return {
      options: {
        scales: {
          y: {
            ticks: {
              mirror: isMobile,
              labelOffset: isMobile ? -15 : -10,
              padding: isMobile ? 0 : 18,
              align: isMobile ? "end" : "start",
              font: {
                size: isMobile ? 12 : 20,
                family: cavalryFontFamily,
              },
            },
          },
        },
      }
    };
  }

  isHorizontal() {
    return this.configValue?.options?.indexAxis === 'y'
  }

  valueAxis() {
    return this.isHorizontal() ? 'x' : 'y'
  }

  parsedValue(context) {
    return this.isHorizontal() ? context.parsed.x : context.parsed.y
  }

  hasMixedDatasetFormats(config) {
    const datasets = config?.data?.datasets
    if (!datasets || datasets.length < 2) return false
    const formats = datasets.map(d => d.valueFormat).filter(Boolean)
    return formats.length > 0 && datasets.some(d => d.valueFormat !== datasets[0].valueFormat)
  }

  perDatasetFormatConfig(config) {
    const datasets = config?.data?.datasets || []
    const yFormat = datasets.find(d => !d.yAxisID)?.valueFormat
    const y1Format = datasets.find(d => d.yAxisID === 'y1')?.valueFormat
    const formatConfig = { options: { scales: {}, plugins: { tooltip: { callbacks: {} } } } }

    if (yFormat) {
      formatConfig.options.scales.y = { ticks: { callback: this.tickFormatter(yFormat) } }
    }
    if (y1Format) {
      formatConfig.options.scales.y1 = { ticks: { callback: this.tickFormatter(y1Format) } }
    }

    formatConfig.options.plugins.tooltip.callbacks.label = (context) => {
      const label = context.dataset.label || ''
      const prefix = label ? label + ': ' : ''
      const fmt = context.dataset.valueFormat
      const val = this.parsedValue(context)
      return prefix + this.formatDisplayValue(val, fmt)
    }

    return formatConfig
  }

  tickFormatter(format) {
    if (format === 'currency') return (value) => "$" + value.toLocaleString('en-US')
    if (format === 'percentage') return (value) => value + "%"
    if (format === 'integer') return (value) => value.toLocaleString('en-US', { maximumFractionDigits: 0 })
    if (format === 'string') return (value) => String(value)
    return (value) => value.toLocaleString('en-US')
  }

  formatDisplayValue(value, format) {
    if (format === 'currency') return "$" + value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    if (format === 'percentage') return value.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + "%"
    if (format === 'integer') return value.toLocaleString('en-US', { maximumFractionDigits: 0 })
    if (format === 'string') return String(value)
    return value.toLocaleString('en-US')
  }

  isRadial() {
    return ['pie', 'doughnut'].includes(this.configValue?.type)
  }

  currencyConfig() {
    const tooltip = {
      callbacks: {
        label: (context) => {
          const label = context.dataset.label || ''
          const prefix = label ? label + ': ' : ''
          if (context.chart.config.type === 'doughnut' || context.chart.config.type === 'pie') {
            return prefix + "$" + context.raw.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
          }
          const val = this.parsedValue(context)
          return prefix + "$" + val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        }
      }
    }
    if (this.isRadial()) return { options: { plugins: { tooltip } } }

    const axis = this.valueAxis()
    return {
      options: {
        scales: {
          [axis]: {
            ticks: {
              callback: function(value) {
                return "$" + value.toLocaleString('en-US')
              }
            }
          }
        },
        plugins: { tooltip }
      }
    }
  }

  percentageConfig() {
    const tooltip = {
      callbacks: {
        label: (context) => {
          const label = context.dataset.label || ''
          const prefix = label ? label + ': ' : ''
          if (context.chart.config.type === 'doughnut' || context.chart.config.type === 'pie') {
            return prefix + context.raw.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + "%"
          }
          const val = this.parsedValue(context)
          return prefix + val.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + "%"
        }
      }
    }
    if (this.isRadial()) return { options: { plugins: { tooltip } } }

    const axis = this.valueAxis()
    return {
      options: {
        scales: {
          [axis]: {
            ticks: {
              callback: function(value) {
                return value + "%"
              }
            }
          }
        },
        plugins: { tooltip }
      }
    }
  }
}
